import { useEffect, useState } from "react";
import { Search, Loader2, ImageIcon, FolderInput, Star } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const thumbUrl = (path) => `${BACKEND_URL}/api/dropbox/thumbnail?path=${encodeURIComponent(path)}`;

const Suggestion = ({ s, busy, onPick }) => (
  <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-2 py-1.5" data-testid="dbx-logo-suggestion">
    <span className="flex h-8 w-10 shrink-0 items-center justify-center overflow-hidden rounded bg-white/90">
      <img src={thumbUrl(s.path)} alt={s.name} className="max-h-8 max-w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1 text-[11px] font-semibold text-amber-300"><Star className="h-3 w-3" /> Saved logo{s.client_name ? ` for ${s.client_name}` : ""}</div>
      <div className="truncate text-[11px] text-muted-foreground" title={s.path}>{s.name}</div>
    </div>
    <button onClick={() => onPick(s)} disabled={!!busy} data-testid="dbx-logo-suggestion-copy"
      className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 text-[11px] font-semibold text-amber-300 transition-colors hover:bg-amber-500/20 disabled:opacity-50">
      {busy === s.path ? <Loader2 className="h-3 w-3 animate-spin" /> : <FolderInput className="h-3 w-3" />} Copy
    </button>
  </div>
);

const LogoResult = ({ r, idx, busy, onPick }) => {
  const [broken, setBroken] = useState(false);
  return (
    <button onClick={() => onPick(r)} disabled={!!busy} title={r.path} data-testid={`dbx-logo-result-${idx}`}
      className="group flex flex-col items-center gap-1 rounded-lg border border-border bg-secondary/30 p-1.5 text-left transition-colors hover:border-blue-500/60 disabled:opacity-50">
      <span className="flex h-12 w-full items-center justify-center overflow-hidden rounded bg-white/90">
        {busy === r.path ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          : broken ? <ImageIcon className="h-5 w-5 text-slate-400" />
          : <img src={thumbUrl(r.path)} alt={r.name}
              loading="lazy" onError={() => setBroken(true)} className="max-h-12 max-w-full object-contain" />}
      </span>
      <span className="w-full truncate text-[10px] text-muted-foreground group-hover:text-foreground">{r.name}</span>
    </button>
  );
};

export const DropboxLogoSearch = ({ orderId, videoFolder }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    api.get(`/orders/${orderId}/dropbox-logo-suggestion`).then(({ data }) => setSuggestion(data.suggestion)).catch(() => {});
  }, [orderId]);

  const search = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${orderId}/dropbox-logos`, { params: { q: q.trim() } });
      setResults(data.results || []);
    } catch (err) { toast.error(apiError(err)); setResults([]); } finally { setLoading(false); }
  };

  const pick = async (r) => {
    setBusy(r.path);
    try {
      const { data } = await api.post(`/orders/${orderId}/dropbox-logo-copy`, { path: r.path });
      toast.success(`Copied ${r.name} to ${data.folder.split("/").pop()}`);
      setSuggestion((s) => ({ path: r.path, name: r.name, client_name: s?.client_name }));
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };

  return (
    <div className="mt-3 border-t border-border pt-3" data-testid="dbx-logo-search">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-xs font-medium text-muted-foreground">Add logo to video folder</span>
        <p className="min-w-0 truncate text-[11px] text-muted-foreground" data-testid="dbx-logo-target" title={videoFolder || ""}>
          {videoFolder ? <>→ <span className="text-foreground">{videoFolder.split("/").pop()}</span></> : "No Video folder linked yet. Run the folder search above first."}
        </p>
      </div>
      <div className={`mt-2 grid gap-2 ${suggestion ? "sm:grid-cols-2" : ""}`}>
      {suggestion && <Suggestion s={suggestion} busy={busy} onPick={pick} />}
      <form onSubmit={search} className="flex items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search logo by name…" data-testid="dbx-logo-input"
          className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2.5 text-xs outline-none focus:border-blue-500/60" />
        <button type="submit" disabled={loading || q.trim().length < 2} data-testid="dbx-logo-search-btn"
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-blue-500/40 bg-blue-500/10 px-3 text-xs font-semibold text-blue-300 transition-colors hover:bg-blue-500/20 disabled:opacity-50">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Search
        </button>
      </form>
      </div>
      {results && (results.length ? (
        <>
          <p className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground"><FolderInput className="h-3 w-3" /> Click a logo to copy it into the Video folder</p>
          <div className="mt-1.5 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6" data-testid="dbx-logo-results">
            {results.map((r, i) => <LogoResult key={r.path} r={r} idx={i} busy={busy} onPick={pick} />)}
          </div>
        </>
      ) : <p className="mt-2 text-xs text-muted-foreground" data-testid="dbx-logo-empty">No images found.</p>)}
    </div>
  );
};
